import { useCallback } from "react";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import { useOpeningHours } from "@/hooks/use-opening-hours";
import { useClosures } from "@/hooks/use-closures";
import { dayjs } from "@/utils/date";

/** Predicate for the shadcn ``<Calendar disabled={…}>`` prop that
 *  rejects dates we can't actually book at the active location:
 *    1. Past dates (in the location's timezone).
 *    2. Days of the week with no ``OpeningHours`` row (restaurant
 *       doesn't open that weekday).
 *    3. Full-day ``LocationClosure`` entries covering the date.
 *
 *  Partial closures (with ``start_time`` / ``end_time``) are NOT
 *  rejected at the day level — the time-slot picker filters those
 *  via the availability endpoint instead.
 *
 *  Single source of truth so the create-reservation modal and the
 *  reservation-edit picker stay in sync. Drift between them is
 *  exactly the bug this hook exists to prevent. */
export function useCalendarDisabled() {
  const { data: openingHours } = useOpeningHours();
  const { data: closures } = useClosures();
  const locationTz = useActiveLocationTimezone();

  return useCallback(
    (date: Date): boolean => {
      const dateStr = dayjs(date).format("YYYY-MM-DD");
      const todayLocal = (locationTz ? dayjs().tz(locationTz) : dayjs()).format("YYYY-MM-DD");
      if (dateStr < todayLocal) return true;

      if (closures) {
        for (const c of closures) {
          const start = c.date;
          const end = c.end_date ?? start;
          if (dateStr >= start && dateStr <= end && !c.start_time && !c.end_time) {
            return true;
          }
        }
      }

      if (openingHours) {
        // dayjs.day(): 0=domingo … 6=sábado.
        // Backend day_of_week: 0=lunes … 6=domingo. Convierto.
        const jsDow = dayjs(date).day();
        const backendDow = jsDow === 0 ? 6 : jsDow - 1;
        const opens = openingHours.some((h) => h.day_of_week === backendDow);
        if (!opens) return true;
      }

      return false;
    },
    [openingHours, closures, locationTz],
  );
}
